import { Web3Provider as Provider, JsonRpcSigner } from '@ethersproject/providers'
import { getNetwork, isSupportedNetwork, SupportedNetwork } from '@spritz-finance/sdk'
import WalletConnectProvider from '@walletconnect/ethereum-provider'
import { useWeb3React, Web3ReactHooks, Web3ReactProvider } from '@web3-react/core'
import { Connector, Provider as ConnectorProvider } from '@web3-react/types'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { NETWORK_SWITCHED } from '../analytics/events'
import { useAnalytics } from '../context/useAnalytics'
import { Connection } from './connection/connection'
import { RPC_URLS } from './connection/constants'
import useEagerlyConnect from './connection/useEagerlyConnect'
import useOrderedConnections from './connection/useOrderedConnections'
import { getConnectionName } from './connection/utils'

const checkProviderUrl = async (provider: ConnectorProvider) => {
    const chainId = (await provider.request({ method: 'eth_chainId' })) as number
    const expectedUrl = chainId ? RPC_URLS[chainId]?.[0] ?? null : null
    if (!expectedUrl) return

    // it's javascript so I can actually do whatever I like
    if (
        // @ts-ignore
        expectedUrl !== provider.http.connection.url
    ) {
        // @ts-ignore
        provider.http.connection.url = expectedUrl
    }
}

type IWeb3ConnectionContext =
    | {
          provider?: Provider
          connector: Connector
          chainId?: number
          account?: string
          connected: boolean
          polygonConnected: boolean
          connectedNetwork: SupportedNetwork | null
          signer?: JsonRpcSigner
      }
    | undefined

const Web3ConnectionState = React.createContext<IWeb3ConnectionContext>(undefined)

export const useWeb3Connection = () => {
    const context = useContext(Web3ConnectionState)
    if (!context)
        throw new Error('Cannot use useWeb3Connection outside of a <SpritzWeb3Provider />')
    return context
}

const getConnectedNetwork = (chainId?: number) => {
    return chainId && isSupportedNetwork(chainId)
        ? (getNetwork(chainId) as SupportedNetwork) ?? null
        : null
}

const SpritzWeb3Provider = ({ children }: { children: React.ReactNode }) => {
    const { trackAnalyticsEvent } = useAnalytics()
    const { provider, connector, chainId, account } = useWeb3React()
    const [signer, setSigner] = useState<JsonRpcSigner>()
    const currentChainId = useRef<number | undefined>(chainId)

    const handleActivate = useCallback(async (provider: Provider) => {
        if (provider.provider instanceof WalletConnectProvider)
            await checkProviderUrl(provider.provider as unknown as ConnectorProvider)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (provider && account) {
            handleActivate(provider)
            const signer = provider.getSigner(account)
            setSigner(signer)
        }
    }, [account, handleActivate, provider])

    useEffect(() => {
        if (!account || chainId === currentChainId.current) return
        const network = getConnectedNetwork(chainId)
        currentChainId.current = chainId
        trackAnalyticsEvent(NETWORK_SWITCHED, { network })
    }, [chainId, account, trackAnalyticsEvent])

    const polygonConnected = useMemo(() => chainId === 137, [chainId])

    const connectedNetwork = getConnectedNetwork(chainId)
    const value = useMemo(
        () => ({
            provider,
            connector,
            chainId,
            account,
            connected: !!account,
            polygonConnected,
            connectedNetwork,
            signer,
        }),
        [provider, connector, chainId, account, polygonConnected, connectedNetwork, signer]
    )
    return <Web3ConnectionState.Provider value={value}>{children}</Web3ConnectionState.Provider>
}

export const Web3Provider: React.FC = ({ children }) => {
    useEagerlyConnect()
    const connections = useOrderedConnections()
    const connectors: [Connector, Web3ReactHooks][] = connections.map(({ hooks, connector }) => [
        connector,
        hooks,
    ])

    const key = useMemo(
        () => connections.map(({ type }: Connection) => getConnectionName(type)).join('-'),
        [connections]
    )

    return (
        <Web3ReactProvider connectors={connectors} key={key}>
            <SpritzWeb3Provider>{children}</SpritzWeb3Provider>
        </Web3ReactProvider>
    )
}
